'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { BookOpen, CheckCircle2, Mail, X } from 'lucide-react'
import SuccessTracking from '@/app/services/booking-success/SuccessTracking'
import { playbookUnlockPath } from '@/lib/home-builder-playbook'

export type PlaybookPurchaseNotice = {
  status: 'success'
  sessionId: string
  serviceSlug: string
  serviceName: string
  totalPrice?: string
  customerName?: string
}

export default function HBPurchaseNotice({ notice }: { notice: PlaybookPurchaseNotice }) {
  const [open, setOpen] = useState(true)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    setReady(true)
    fetch(playbookUnlockPath(notice.sessionId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: notice.sessionId }),
    }).catch(() => {})
  }, [notice.sessionId])

  const dismiss = () => {
    setOpen(false)
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.searchParams.delete('booking')
    url.searchParams.delete('session_id')
    url.searchParams.delete('service')
    const next = `${url.pathname}${url.search}${url.hash}`
    history.replaceState(null, '', next)
  }

  if (!ready || !open) return null

  const firstName = notice.customerName?.trim().split(' ')[0] || null

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center px-4 py-10 bg-slate-900/45" role="dialog" aria-modal="true" aria-labelledby="hb-purchase-title">
      <button type="button" className="absolute inset-0 cursor-default" aria-label="Dismiss" onClick={dismiss} />
      <div className="relative w-full max-w-lg rounded-3xl bg-white p-8 sm:p-12 shadow-lg text-center">
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-4 top-4 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <SuccessTracking
          sessionId={notice.sessionId}
          serviceSlug={notice.serviceSlug}
          serviceName={notice.serviceName}
          totalPrice={notice.totalPrice}
          customerName={notice.customerName}
        />

        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
          <CheckCircle2 className="h-10 w-10 text-green-500" />
        </div>

        <h2 id="hb-purchase-title" className="text-2xl font-bold text-slate-900 sm:text-3xl">
          {firstName ? `You're all set, ${firstName}!` : "You're all set!"}
        </h2>
        <p className="mt-3 text-slate-500 leading-relaxed">
          Your payment was successful. Open the playbook now — a backup link is also in your inbox.
        </p>

        <div className="mt-8 rounded-xl bg-slate-50 p-5 text-left space-y-3">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">What happens next</p>
          {[
            { step: '1', text: 'Open the playbook below for the PDF, templates, and CRM packs.' },
            { step: '2', text: 'Your confirmation email has a backup link to the same page.' },
            { step: '3', text: 'Playbook Agent access is included - use it when you hit a decision mid-install.' },
          ].map(({ step, text }) => (
            <div key={step} className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                {step}
              </span>
              <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500">
          <Mail className="h-4 w-4 shrink-0 text-slate-400" />
          Questions? Email us at{' '}
          <a href="mailto:support@smartmetrics.com" className="font-semibold text-blue-600 hover:underline">
            support@smartmetrics.com
          </a>
        </div>

        <a
          href={playbookUnlockPath(notice.sessionId)}
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#0478FB] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0366d6]"
        >
          <BookOpen className="h-4 w-4" />
          Open your playbook
        </a>
      </div>
    </div>,
    document.body,
  )
}
